/**
 * Live Update Service - Self-hosted Capgo integration
 * Handles checking for and applying OTA updates
 */
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';

// Get backend URL - handles web, PWA, and native Capacitor
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  if (Capacitor.isNativePlatform()) {
    return 'https://bizilgo.com';
  }
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    if (protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:') {
      return 'https://bizilgo.com';
    }
    if (protocol === 'https:' || protocol === 'http:') {
      return window.location.origin;
    }
  }
  return 'https://bizilgo.com';
};

const API_BASE = getBackendUrl();

class LiveUpdateService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.initialized = false;
    this.currentVersion = null;
  }

  /**
   * Initialize the live update service
   * Call this when the app starts
   */
  async initialize() {
    if (!this.isNative || this.initialized) {
      console.log('[LiveUpdate] Skipping - not native or already initialized');
      return;
    }

    try {
      // Get current bundle info
      const current = await CapacitorUpdater.current();
      this.currentVersion = current?.bundle?.version || '1.0.0';
      console.log('[LiveUpdate] Current version:', this.currentVersion);

      // Notify that the app is ready (required after update)
      await CapacitorUpdater.notifyAppReady();
      console.log('[LiveUpdate] App ready notified');

      // Set up event listeners
      this.setupListeners();
      
      this.initialized = true;
      
      // Check for updates in background
      this.checkForUpdate();
    } catch (error) {
      console.error('[LiveUpdate] Initialize error:', error);
    }
  }

  /**
   * Set up update event listeners
   */
  setupListeners() {
    // Listen for download progress
    CapacitorUpdater.addListener('download', (info) => {
      console.log('[LiveUpdate] Download progress:', info.percent + '%');
    });

    // Listen for download complete
    CapacitorUpdater.addListener('downloadComplete', (info) => {
      console.log('[LiveUpdate] Download complete:', info.bundle);
    });

    // Listen for update failed
    CapacitorUpdater.addListener('updateFailed', (info) => {
      console.error('[LiveUpdate] Update failed:', info);
    });

    // Listen for download failed
    CapacitorUpdater.addListener('downloadFailed', (info) => {
      console.error('[LiveUpdate] Download failed:', info);
    });
  }

  /**
   * Check for available updates from self-hosted server
   */
  async checkForUpdate() {
    if (!this.isNative) return null;

    try {
      const platform = Capacitor.getPlatform();
      const appId = 'com.bizilgo.customer';
      
      const response = await fetch(
        `${API_BASE}/api/updates/check?app_id=${appId}&version=${this.currentVersion}&platform=${platform}&channel=production`
      );
      
      const data = await response.json();
      
      if (data.url && data.version) {
        console.log('[LiveUpdate] Update available:', data.version);
        return data;
      }
      
      console.log('[LiveUpdate] No update available');
      return null;
    } catch (error) {
      console.error('[LiveUpdate] Check error:', error);
      return null;
    }
  }

  /**
   * Download and apply an update
   * @param {boolean} immediate - If true, apply immediately. If false, apply on next app restart.
   */
  async downloadAndApply(immediate = false) {
    if (!this.isNative) return false;

    try {
      const updateInfo = await this.checkForUpdate();
      
      if (!updateInfo) {
        console.log('[LiveUpdate] No update to download');
        return false;
      }

      console.log('[LiveUpdate] Downloading:', updateInfo.url);
      
      // Download the bundle
      const bundle = await CapacitorUpdater.download({
        url: updateInfo.url,
        version: updateInfo.version
      });

      console.log('[LiveUpdate] Downloaded bundle:', bundle.id);

      if (immediate) {
        // Apply immediately (will reload the app)
        await CapacitorUpdater.set(bundle);
        console.log('[LiveUpdate] Update applied immediately');
      } else {
        // Set to apply on next restart
        await CapacitorUpdater.setDelay({ kind: 'background' });
        console.log('[LiveUpdate] Update will apply on next restart');
      }

      return true;
    } catch (error) {
      console.error('[LiveUpdate] Download/apply error:', error);
      return false;
    }
  }

  /**
   * Get list of downloaded bundles
   */
  async getDownloadedBundles() {
    if (!this.isNative) return [];

    try {
      const list = await CapacitorUpdater.list();
      return list.bundles || [];
    } catch (error) {
      console.error('[LiveUpdate] List error:', error);
      return [];
    }
  }

  /**
   * Reset to the original built-in bundle
   */
  async reset() {
    if (!this.isNative) return;

    try {
      await CapacitorUpdater.reset();
      console.log('[LiveUpdate] Reset to built-in bundle');
    } catch (error) {
      console.error('[LiveUpdate] Reset error:', error);
    }
  }

  /**
   * Get current version info
   */
  async getCurrentVersion() {
    if (!this.isNative) return { version: 'web' };

    try {
      const current = await CapacitorUpdater.current();
      return current?.bundle || { version: 'unknown' };
    } catch (error) {
      console.error('[LiveUpdate] Get current error:', error);
      return { version: 'error' };
    }
  }
}

// Export singleton instance
export const liveUpdateService = new LiveUpdateService();
export default liveUpdateService;
